/**
 * AnnouncementsProvider
 * ---------------------
 * Fetches active announcements for the current user and injects the
 * three UI variants:
 *
 *   • LoginGate      - blocking modal, must be acknowledged
 *   • Banner         - top strip, dismissible per session
 *   • Toast (sonner) - fires once per login
 *
 * Mounted inside the authenticated MainLayout so it only runs for
 * signed-in users. The public sign / login pages skip this entirely.
 *
 * Session dismissal (banners + toasts) is tracked with sessionStorage so
 * they don't reappear on every route change but return on next login.
 *
 * TEST-DRIVE MODE
 * ---------------
 * The admin editor dispatches a `avida:announcement-test-drive` custom
 * event with a draft payload. We prepend that draft to our state with a
 * `_isTest: true` flag; ack/dismiss for test entries stays client-side
 * (no DB writes) so admins can smoke-test before publishing.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import LoginGate from "./LoginGate";
import AnnouncementBanner from "./AnnouncementBanner";

const API = process.env.REACT_APP_BACKEND_URL;
const TEST_EVENT = "avida:announcement-test-drive";

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

function dismissedKey(a) {
  return `avida:ann:dismissed:${a.id}:v${a.version}`;
}

export default function AnnouncementsProvider() {
  const { user } = useAuth();
  const [gates, setGates] = useState([]);
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState({});

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const r = await fetch(`${API}/api/announcements/active`, { headers: authHeader() });
      if (!r.ok) return;
      const data = await r.json();
      // Preserve any existing test-drive entries so a background refetch
      // doesn't blow away a preview the admin just launched.
      setGates((prev) => [...prev.filter((g) => g._isTest), ...(data.gate || [])]);
      setBanners((prev) => [...prev.filter((b) => b._isTest), ...(data.banner || [])]);
      // Fire each toast exactly once per session
      const toasts = data.toast || [];
      toasts.forEach((t) => {
        const key = `avida:ann:toast:${t.id}:v${t.version}`;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, "1");
        toast(t.title, {
          description: t.message,
          duration: 6000,
        });
      });
    } catch {
      /* silent - announcement failures shouldn't break the app */
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Listen for admin editor's "Test on me" event and inject the draft
  // as a client-only announcement. Ack/dismiss stay client-side.
  useEffect(() => {
    const handler = (ev) => {
      const draft = ev.detail;
      if (!draft) return;
      const testAnnouncement = {
        ...draft,
        id: `test-${Date.now()}`,
        version: 0,
        _isTest: true,
      };
      if (draft.display_type === "gate") {
        setGates((prev) => [testAnnouncement, ...prev]);
      } else if (draft.display_type === "banner") {
        setBanners((prev) => [testAnnouncement, ...prev]);
      } else if (draft.display_type === "toast") {
        toast(draft.title || "Preview", {
          description: (draft.message || "") + "  · TEST",
          duration: 6000,
        });
      }
    };
    window.addEventListener(TEST_EVENT, handler);
    return () => window.removeEventListener(TEST_EVENT, handler);
  }, []);

  const handleAck = useCallback(async (a) => {
    // Test-drive entries never touch the server.
    if (a._isTest) {
      setGates((prev) => prev.filter((g) => g.id !== a.id));
      toast.success("Test preview dismissed — no data saved");
      return;
    }
    try {
      await fetch(`${API}/api/announcements/${a.id}/acknowledge`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ version: a.version }),
      });
      setGates((prev) => prev.filter((g) => g.id !== a.id));
    } catch {
      toast.error("Could not save your acknowledgment. Please try again.");
    }
  }, []);

  const handleDismissBanner = useCallback((a) => {
    if (a._isTest) {
      setBanners((prev) => prev.filter((b) => b.id !== a.id));
      return;
    }
    sessionStorage.setItem(dismissedKey(a), "1");
    setDismissed((prev) => ({ ...prev, [a.id]: true }));
  }, []);

  if (!user) return null;

  // Only the FIRST unacknowledged gate blocks. Subsequent ones show
  // after the first is acknowledged (rare in practice - usually 1).
  const activeGate = gates[0];
  const activeBanners = banners.filter((b) => !dismissed[b.id] && !sessionStorage.getItem(dismissedKey(b)));

  return (
    <>
      {activeGate && (
        <LoginGate announcement={activeGate} onAcknowledge={() => handleAck(activeGate)} />
      )}
      {activeBanners.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-40 pointer-events-none flex flex-col gap-1">
          {activeBanners.map((b) => (
            <div key={b.id} className="pointer-events-auto">
              <AnnouncementBanner announcement={b} onDismiss={() => handleDismissBanner(b)} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
